import { Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { FlashMessage } from '@/components/ui/FlashMessage';

interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

interface MissiveData {
    id: number;
    subject: string;
    content_preview: string;
    sender: { id: number; username: string } | null;
    recipient: { id: number; username: string } | null;
    status: { value: string; label: string };
    sent_at: string | null;
    created_at: string;
}

interface PaginatedMissives {
    data: MissiveData[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    links: PaginationLink[];
}

interface Props {
    missives: PaginatedMissives;
}

function formatDate(isoString: string | null): string {
    if (!isoString) {
        return 'Unknown';
    }

    return new Date(isoString).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const STATUS_BADGES: Record<string, string> = {
    sent: 'bg-blue-600 text-blue-100',
    read: 'bg-green-600 text-green-100',
    archived: 'bg-slate-600 text-slate-100',
};

export default function Missives({ missives }: Props) {
    return (
        <GmLayout title="All Missives" activePath="/gm/missive-reports">
            <FlashMessage />

            <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">📬 All Missives</h1>
                    <Link
                        href="/gm/missive-reports"
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                        Back to Reports
                    </Link>
                </div>

                <div className="mb-4 text-sm text-slate-400">
                    {missives.total} total missive{missives.total === 1 ? '' : 's'}
                </div>

                {missives.data.length === 0 ? (
                    <div className="text-center py-12 text-slate-400 bg-slate-800 rounded-lg">
                        No missives found.
                    </div>
                ) : (
                    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-slate-700">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Sent</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Message</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Participants</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">Status</th>
                                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {missives.data.map((missive) => (
                                    <tr key={missive.id} className="hover:bg-slate-700/50">
                                        <td className="px-4 py-3 text-sm text-slate-300 whitespace-nowrap">
                                            {formatDate(missive.sent_at ?? missive.created_at)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-white font-medium truncate max-w-sm">
                                                {missive.subject}
                                            </div>
                                            <div className="text-sm text-slate-400 truncate max-w-sm">
                                                {missive.content_preview}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-300">
                                            {missive.sender?.username || 'Unknown'} → {missive.recipient?.username || 'Unknown'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 text-xs rounded-full ${STATUS_BADGES[missive.status.value] || 'bg-slate-600 text-slate-100'}`}>
                                                {missive.status.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Link
                                                href={`/missives/${missive.id}`}
                                                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 rounded text-sm text-white transition-colors"
                                            >
                                                Open
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {missives.last_page > 1 && (
                    <div className="mt-6 flex justify-center gap-1">
                        {missives.links.map((link, index) => (
                            <Link
                                key={index}
                                href={link.url || '#'}
                                className={`px-3 py-1 rounded text-sm ${
                                    link.active
                                        ? 'bg-amber-600 text-white'
                                        : link.url
                                        ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                                        : 'bg-slate-800 text-gray-500 cursor-not-allowed pointer-events-none'
                                }`}
                                dangerouslySetInnerHTML={{ __html: link.label }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </GmLayout>
    );
}
